import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { API_URL_COMPANIES } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';

import { AiAgent, AiSistant, KnowledgeDocument } from './shared/models';

@Injectable({
  providedIn: 'root',
})
export class AlcmeonAiService {
  routingTagsMap: Map<number, string> = new Map();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService,
    private http: HttpClient,
    private searchBarService: SearchBarService
  ) {
    const user = this.alcmeonContextService.self;
    user.routing_tags.forEach(tag => {
      this.routingTagsMap.set(tag.id, tag.name);
    });
  }

  getAiAgents = (buildCriteria?: any): Observable<HttpResponse<AiAgent[]>> => {
    const body = buildCriteria || this.searchBarService.buildCriteria();
    return this.http
      .post<AiAgent[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/ai-agents`, body, {
        observe: 'response',
      })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  };

  getAiAgent(id: number): Observable<AiAgent> {
    return this.http
      .get<AiAgent>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/ai-agent/${id}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  createAiAgent(data: AiAgent): Observable<AiAgent> {
    return this.http
      .post<AiAgent>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/ai-agent`, data)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  updateAiAgent(id: number, data: AiAgent): Observable<AiAgent> {
    return this.http
      .put<AiAgent>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/ai-agent/${id}`, data)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  deleteAiAgent(agentId: number): Observable<void> {
    return this.http
      .delete<void>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/ai-agent/${agentId}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  isGaiaConfigExists(): Observable<boolean> {
    return this.http
      .get<boolean>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/gaia-config-exists-and-enabled`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getKnowledgeDocsCountersForAiAgents(): Observable<{ [id: number]: number }> {
    return this.http
      .get<{ [id: number]: number }>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-document-counter`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getKnowledgeDocuments = (): Observable<KnowledgeDocument[]> => {
    return this.http
      .get<KnowledgeDocument[]>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-documents`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  };

  getKnowledgeDocument(uuid: string): Observable<KnowledgeDocument> {
    return this.http
      .get<KnowledgeDocument>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-document/${uuid}`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  deleteKnowledgeDocument(documentUuid: string): Observable<void> {
    return this.http
      .delete<void>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-document/${documentUuid}`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  createKnowledgeDocument(data: KnowledgeDocument): Observable<KnowledgeDocument> {
    return this.http
      .post<KnowledgeDocument>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-document`,
        data
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  updateKnowledgeDocument(id: string, data: KnowledgeDocument): Observable<KnowledgeDocument> {
    return this.http
      .put<KnowledgeDocument>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-document/${id}`,
        data
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getPdf(uuid: string): Observable<any> {
    return this.http
      .get<any>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-document/pdf/${uuid}`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  createKnowledgeDocumentPdf(formValues: any, file: File): Observable<any> {
    if (!file) {
      return;
    }

    const { type, name, size } = file;
    const formData = new FormData();

    formData.set('mediaType', 'PDF');
    formData.set('mimeType', type);
    formData.set('fileName', name);
    formData.set('size', size.toString());
    formData.set('file', file);

    formData.set('isActive', formValues.isActive);
    formData.set('name', formValues.name);
    formData.set('sourceType', formValues.sourceType);
    formData.set('usedByAiAgentsIds', formValues.usedByAiAgentsIds);

    return this.http
      .post<KnowledgeDocument>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-document/pdf`,
        formData
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  updateKnowledgeDocumentPdf(uuid: string, formValues: any, file?: File): Observable<any> {
    const formData = new FormData();
    if (file) {
      const { type, name, size } = file;
      formData.set('mediaType', 'PDF');
      formData.set('mimeType', type);
      formData.set('fileName', name);
      formData.set('size', size.toString());
      formData.set('file', file);
    }

    formData.set('isActive', formValues.isActive);
    formData.set('name', formValues.name);
    formData.set('sourceType', formValues.sourceType);
    formData.set('usedByAiAgentsIds', formValues.usedByAiAgentsIds);

    return this.http
      .put<KnowledgeDocument>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/knowledge-document/pdf/${uuid}`,
        formData
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getAissistantSetups = (): Observable<HttpResponse<AiSistant[]>> => {
    return this.http
      .get<AiSistant[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/aissistants`, {
        observe: 'response',
      })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  };

  saveAissistantSetups(aiSistants: AiSistant[]): Observable<any> {
    return this.http
      .post<any>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/aissistants`, aiSistants)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }
}
